<template>
  <v-container
    class="ma-0 pt-0 px-0"
    style="max-width :100vw;"
    :class="[$vuetify.breakpoint.smAndDown ? 'mt-12' : '']"
  >
    <v-row class="ma-0" justify="center" style="position: relative">
      <v-btn
        v-if="routerHistory || workingAsComponent"
        :style="[backBtnPosition]"
        fab
        :small="workingAsComponent || $vuetify.breakpoint.smAndDown"
        color="brand"
        class="white--text "
        medium
        depressed
        @click="back"
      >
        <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
      </v-btn>

      <v-col
        cols="12"
        md="9"
        class="pa-0"
        :style="[
          $vuetify.breakpoint.mdAndUp
            ? { maxWidth: '65vw' }
            : $vuetify.breakpoint.sm
            ? { maxWidth: '90%', margin: 'auto' }
            : workingAsComponent
            ? { maxWidth: '100%', margin: 'auto' }
            : { maxWidth: '90%', margin: 'auto' }
        ]"
      >
        <!-- Project Information -->
        <v-row
          v-if="!workingAsComponent"
          align="center"
          class="profile-profile flex-column ma-0 pa-0"
        >
          <project-section
            :project="projectToDisplay"
            :projectLeaderEmail="projectLeaderEmail"
            :projectHashtags="projectHashtags"
          ></project-section>
        </v-row>

        <!-- Showcase Information -->
        <v-row
          v-if="documentById.hasOwnProperty('document')"
          align="center"
          class="profile-showcase ma-0 mb-2"
          :class="workingAsComponent ? 'mt-0' : 'mt-2'"
          justify="center"
        >
          <v-col class="pa-0 ">
            <showcase-section
              :documentById="documentById"
              :key="documentKey"
              :documentKey="documentKey"
              :projectHashtags="projectHashtags"
            ></showcase-section>
          </v-col>
        </v-row>

        <!-- Links to Section -->
        <v-row class="ma-0">
          <links-to-section :linkedItems="linkedItems"></links-to-section>
        </v-row>

        <!-- Profile Information -->
        <v-row
          v-if="documentById.hasOwnProperty('profile')"
          align="center"
          class="profile-profile flex-column ma-0 mt-1 pa-0"
        >
          <creator-info-section
            :profile="documentById.profile"
            :documentDefaultLang="documentDefaultLang"
          ></creator-info-section>
        </v-row>
      </v-col>
    </v-row>

    <!-- Dialog for informing the user about accepting or suggesting edits -->
    <base-message-dialog
      v-if="showApprovalMsgDialog"
      :show="showApprovalMsgDialog"
    >
      <template v-slot:card-image>
        <accept-items
          :width="$vuetify.breakpointSmAndUp ? '300' : '200'"
          class="mt-2"
        ></accept-items>
      </template>

      <template v-slot:title>
        <h3 class="mt-2">{{ $t('yourShowcaseIsCompleted') }}</h3>
      </template>

      <template v-slot:message>
        <p class="mt-2">
          {{ $t('ksCompletedShowcaseMsg') }}
        </p>
      </template>

      <template v-slot:card-action>
        <v-btn @click="showApprovalMsgDialog = false" text>{{
          $t('understood')
        }}</v-btn>
      </template>
    </base-message-dialog>

    <!-- Dialog for collecting suggestion -->
    <base-message-dialog
      v-if="showAddSuggestionsDialog"
      :show="showAddSuggestionsDialog"
    >
      <template v-slot:title>
        <div class="pr-4">
          <h3>
            {{ $t('addSuggestions') }}
          </h3>

          <span>
            {{ $t('pleaseBeDetailed') }}
          </span>
        </div>
      </template>

      <template v-slot:message>
        <v-textarea
          v-model="suggestions"
          solo
          flat
          background-color="inputBg"
          :placeholder="$t('writeContent')"
          auto-grow
          clearable
          hide-details
        ></v-textarea>
      </template>

      <template v-slot:card-action>
        <v-btn
          text
          @click="(showAddSuggestionsDialog = false), (suggestions = '')"
        >
          {{ $t('cancel') }}
        </v-btn>

        <v-btn
          @click="
            documentApprovalHandler('REJECTED'),
              (showAddSuggestionsDialog = false)
          "
          depressed
          color="error"
          class="white--text"
          :disabled="suggestions === ''"
          ><v-icon class="mr-2">{{ icons.mdiTextBoxRemove }}</v-icon
          ><span>{{ $t('suggestEdits') }}</span></v-btn
        >
      </template>
    </base-message-dialog>

    <!-- Actions for accepting rejecting showcase while on document-approval -->
    <v-sheet
      v-if="viewForApproval"
      color="white"
      height="65"
      width="100vw"
      tile
      class="px-8"
      style="position: sticky; bottom: 0; left: 0; box-shadow: 0px -3px 6px 0px rgba(0, 0, 0, .16)"
    >
      <v-row no-gutters align="center" justify="end" class="fill-height">
        <v-btn
          v-if="!aproveLoader"
          @click="showAddSuggestionsDialog = true"
          color="error"
          class="mr-4 white--text"
          depressed
          >{{ $t('suggestEdits') }}</v-btn
        >
        <v-btn
          v-if="!aproveLoader"
          @click="documentApprovalHandler('ACCEPTED')"
          color="brand"
          class="white--text"
          depressed
          >{{ $t('accept') }}</v-btn
        >

        <v-btn
          v-if="aproveLoader"
          width="140px"
          depressed
          :loading="aproveLoader"
        ></v-btn>
      </v-row>
    </v-sheet>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import store from '@/store/store';
import { profiles, documents } from '@/utilities/api';

import CreatorInfoSection from './CreatorInfoSection.vue';
import ShowcaseSection from './ShowcaseSection.vue';
import ProjectSection from './ProjectSection.vue';
import LinksToSection from './LinksToSection.vue';

import { MapMixin } from '@/utilities/mixins/MapMixin';
import { TemplateArrayHelpers } from '@/utilities/mixins/TemplateArrayHelpers';
import { LanguageHelpers } from '@/utilities/mixins/LanguageHelpers';

import { mdiTextBoxRemove, mdiArrowLeft } from '@mdi/js';
import Swal from 'sweetalert2';

const AcceptItems = () => import('../../assets/SvgComponents/AcceptItems.vue');

const BaseMessageDialog = () =>
  import(
    '@/components/base_components/BaseMessageDialog/BaseMessageDialog.vue'
  );

export default {
  name: 'ViewShowcaseView',

  metaInfo() {
    return {
      title: this.metaTitle,
      meta: [{ name: 'description', content: this.metaDescription }]
    };
  },

  components: {
    CreatorInfoSection,
    ProjectSection,
    ShowcaseSection,
    BaseMessageDialog,
    AcceptItems,
    LinksToSection
  },

  props: {
    documentIdProp: {
      type: String,
      required: false
    },

    documentKey: {
      type: String,
      required: false,
      default: 'documentKey'
    }
  },

  data() {
    return {
      routerHistory: null,
      userProfileProjects: {},
      creativityItems: [],
      profileItems: [],
      documentItems: [],
      showApprovalMsgDialog: false,
      showAddSuggestionsDialog: false,
      suggestions: '',
      aproveLoader: false,
      actionTaken: false,
      workingAsComponent: false,
      segregationComplete: false,
      metaTitle: 'TRUST Showcase',
      metaDescription: 'Showcase',
      projectToDisplay: {},
      projectLeaderEmail: ''
    };
  },

  async created() {
    const { document, profile } = this.documentById;

    this.checkAndAttachMapScript(() => {});
    this.workingAsComponent = this.documentIdProp !== undefined;

    this.setProjectToDisplay();

    if (!this.workingAsComponent) {
      this.metaTitle = document.name;
      this.metaDescription = `${profile.name} ${this.$t(
        'userSubmittedShowcase'
      )} ${document.name} ${this.$t('toProject')} ${this.findTranslation(
        this.getProjectName()
      )}. ${this.$t('visitToLearnMore')}`;
    }

    const urlLang = this.$route?.params?.lang;
    if (urlLang) {
      await this.setUrlLanguage(urlLang);
    }

    if (this.workingAsComponent) {
      const payload = {
        documentId: this.documentIdProp
      };

      await this.getDocumentById(payload);
      await this.segregateLinkedItems();
    }

    if (this.userProfile?.profile?.profileId) this.getUserProfileProjects();
  },

  destroyed() {
    this.setDocumentById({});
  },

  mixins: [TemplateArrayHelpers, LanguageHelpers, MapMixin],

  methods: {
    ...mapActions(['getDocumentById', 'setDocumentById']),

    back() {
      if (this.workingAsComponent) {
        return this.$emit('back');
      }

      this.$router.go(-1);
    },

    setProjectToDisplay() {
      //if query projectId and document is copied
      if (this.$route?.query?.projectId && this.documentById.document.copied) {
        const project = this.documentById.connectedProjects.find(
          project =>
            project.projectId.replace('#', '') === this.$route?.query?.projectId
        );

        // if has connectected project
        if (project) {
          this.projectToDisplay = project;
          this.projectLeaderEmail = this.findTranslation(
            project.projectLeaderEmailContact,
            this.defaultLanguage
          );
          return;
        }
      }

      // else set original project and project leader email
      this.projectToDisplay = this.documentById.project;
      this.projectLeaderEmail = this.getProjectLeaderEmail();
    },

    getProjectLeaderEmail() {
      if (!this.documentById?.projectLeaderProfile?.template) return '';
      const emailField = this.documentById.projectLeaderProfile.template[0].fields.find(
        field => field.basicInfoFieldName === 'emailContact'
      );

      return this.findTranslation(emailField.value, this.defaultLanguage);
    },

    getProjectName() {
      if (!this.projectToDisplay?.template) {
        return typeof this.projectToDisplay.name === 'string'
          ? this.projectToDisplay.name
          : this.findTranslation(
              this.projectToDisplay.name,
              this.defaultLanguage
            );
      }

      return this.findTranslation(
        this.projectToDisplay.template[0].name,
        this.defaultLanguage
      );
    },

    async getUserProfileProjects() {
      const profileId = this.userProfile.profile.profileId.replace('#', '');
      const res = await profiles.getProjects(profileId, {
        language: this.defaultLanguage
      });

      this.userProfileProjects = res;
    },

    segregateLinkedItems() {
      const { template } = this.computeDocumentById.document;

      //All LinksTo Fields
      const allLinksToFields = template
        .map((section, sectionIndex) =>
          section.fields.map((field, fieldIndex) => {
            return { ...field, sectionIndex, fieldIndex };
          })
        )
        .flat()
        .filter(field => field.type === 'LINKSTO');

      //All items in all LinksTo Fields
      let allListItems = allLinksToFields
        .map(item => {
          return this.findTranslation(item.value, this.defaultLanguage);
        })
        .flat();

      //used for older profiles that does not have the linksTo field
      allListItems = allListItems.filter(item => item !== null);

      this.creativityItems = allListItems.filter(
        field => field.type === 'CREATIVITY'
      );

      this.profileItems = allListItems.filter(
        field => field.type === 'PROFILE'
      );

      this.documentItems = allListItems.filter(
        field => field.type === 'DOCUMENT'
      );

      this.segregationComplete = true;
    },

    async documentApprovalHandler(status) {
      this.aproveLoader = true;

      const documentId = this.$route.params.id;
      const payload = {
        status: status
      };

      if (status === 'REJECTED') payload['cause'] = this.suggestions;

      try {
        await documents.documentApproval(documentId, payload);

        Swal.fire({
          title: this.$t('success'),
          text: `${this.$t(
            'youHaveSuccesfully'
          )} ${status.toLowerCase()} ${this.$t('yourShowcase')}`,
          type: 'success',
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          timer: 3000
        });

        this.actionTaken = true;
        this.aproveLoader = false;
      } catch (e) {
        Swal.fire({
          title: this.$t('error'),
          text: `${this.$t('thereWasError')} ${status.toLowerCase()} ${this.$t(
            'plaseTryAgainOtherTime'
          )}`,
          type: 'error',
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          timer: 3000
        });

        this.aproveLoader = false;
      }
    }
  },

  computed: {
    ...mapState({
      documentById: state => state.documents.documentById,
      userProfile: state => state.authentication.userProfile,
      defaultLanguage: state => state.defaultLanguage
    }),

    documentDefaultLang() {
      return this.documentById?.document?.defaultLanguage;
    },

    backBtnPosition() {
      if (this.workingAsComponent) {
        if (this.$vuetify.breakpoint.xs) {
          return { position: 'absolute', top: '-44px', left: '0px' };
        } else if (this.$vuetify.breakpoint.sm) {
          return { position: 'absolute', top: '-44px', left: '0px' };
        } else {
          return { position: 'absolute', top: '0px', left: '0px' };
        }
      }

      if (this.$vuetify.breakpoint.smAndDown)
        return {
          position: 'absolute',
          top: '-52px',
          left: '20px'
        };

      return { position: 'absolute', top: '0px', left: '48px' };
    },

    linkedItems() {
      if (!this.segregationComplete) return [];
      const linkedItems = [];

      if (this.creativityItems?.length > 0) {
        const items = this.creativityItems.map(item => {
          return {
            name: item.name,
            logo: item.logo,
            profileId: item.profile.profileId,
            documentId: item.id,
            available: item.available,
            type: 'creativity'
          };
        });

        const creativities = { name: 'linkedCreativities', items: items };

        linkedItems.push(creativities);
      }

      if (this.profileItems?.length > 0) {
        const items = this.profileItems.map(item => {
          return {
            name: item.name,
            logo: item.logo,
            profileId: item.id,
            available: item.available,
            type: 'profile'
          };
        });

        const profiles = { name: 'linkedProfiles', items: items };

        linkedItems.push(profiles);
      }

      if (this.documentItems?.length > 0) {
        const items = this.documentItems.map(item => {
          return {
            name: item.name,
            logo: item.logo,
            profileId: item.profile.profileId,
            documentId: item.id,
            available: item.available,
            type: 'document'
          };
        });

        const documents = { name: 'linkedShowcases', items: items };

        linkedItems.push(documents);
      }

      return linkedItems;
    },

    icons: () => ({
      mdiTextBoxRemove,
      mdiArrowLeft
    }),

    viewForApproval() {
      if (this.workingAsComponent) return false;
      const docStatus = this.documentById.document.status;

      const showcaseCreator =
        this.documentById.profile.profileId ===
        this.userProfile?.profile?.profileId;

      if (
        this.$route.path.includes('document-approval') &&
        docStatus === 'COMPLETED' &&
        !this.actionTaken &&
        showcaseCreator
      )
        return true;

      return false;
    },

    computeDocumentById() {
      return this.documentById;
    },

    projectHashtags() {
      if (this.projectToDisplay?.hashtags)
        return this.projectToDisplay?.hashtags
          ? this.projectToDisplay.hashtags
          : [];

      return this.documentById?.project?.hashtags
        ? this.documentById?.project?.hashtags
        : [];
    }
  },

  watch: {
    defaultLanguage(newVal) {
      //return if being used as component
      if (this.workingAsComponent) return;
      const { id, lang } = this.$route.params;
      this.$router.replace({
        name: '',
        params: {
          id,
          lang: newVal
        },
        query: {
          ...this.$route.query
        }
      });
    }
  },

  beforeRouteEnter: async (to, from, next) => {
    const payload = {
      documentId: to.params.id
    };

    const isLoggedIn = store?.state?.authentication?.userProfile.hasOwnProperty(
      'profile'
    );

    if (to.name === 'viewShowcaseForApproval' && !isLoggedIn) {
      next({ name: 'login', query: { routeTo: to.path } });
      return;
    }

    const res = await store.dispatch('getDocumentById', payload);
    if (store?.state?.documents?.documentErrorMsg) {
      next({ name: '404' });
    }

    const docStatus = res?.data?.document?.status;
    next(vm => {
      const showcaseCreator =
        vm.userProfile?.profile?.profileId ===
        vm.documentById.profile.profileId;

      if (from.name) {
        vm.routerHistory = { from };
      }

      if (
        to.name === 'viewShowcaseForApproval' &&
        docStatus === 'COMPLETED' &&
        showcaseCreator
      ) {
        vm.showApprovalMsgDialog = true;
      }

      vm.segregateLinkedItems();
      next();
    });
  },

  async beforeRouteUpdate(to, from, next) {
    if (to.name === 'viewShowcase' || to.name === 'viewShowcaseForApproval') {
      const payload = {
        documentId: to.params.id
      };

      await store.dispatch('getDocumentById', payload);

      await this.segregateLinkedItems();

      next();
    }
  }
};
</script>
